import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Quote, TrendingUp } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";

type Story =
  | { type: "quote"; quote: string; name: string; organization: string; label: string }
  | { type: "metric"; metric: string; title: string; copy: string; label: string };

// The only quoted testimonial is retained verbatim from the supplied Altitude source material.
const stories: Story[] = [
  {
    type: "quote",
    label: "Client perspective",
    quote: "Altitude Fitness Management Group’s insights and support have been invaluable in driving our business forward.",
    name: "Eric Rosas",
    organization: "Marimba Café Co.",
  },
  {
    type: "metric",
    label: "Operator outcomes",
    metric: "20+",
    title: "Operators supported",
    copy: "Fitness businesses supported through launches, operational planning, growth strategy, and more focused management systems.",
  },
  {
    type: "metric",
    label: "Fitness-specific expertise",
    metric: "14+",
    title: "Years in consulting",
    copy: "A practical perspective built around the decisions that keep a fitness business moving—people, operations, revenue, and facilities.",
  },
];

export function TestimonialCarousel() {
  const [api, setApi] = useState<CarouselApi>();
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!api) return;
    const updateSelected = () => setSelected(api.selectedScrollSnap());
    updateSelected();
    api.on("select", updateSelected);
    api.on("reInit", updateSelected);
    return () => {
      api.off("select", updateSelected);
      api.off("reInit", updateSelected);
    };
  }, [api]);

  useEffect(() => {
    if (!api || paused) return;
    const autoplay = window.setInterval(() => api.scrollNext(), 6000);
    return () => window.clearInterval(autoplay);
  }, [api, paused]);

  return <div className="testimonial-carousel" aria-label="Client stories carousel" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
    <div className="testimonial-carousel__bar">
      <p>Client stories</p>
      <div className="testimonial-carousel__controls">
        <button type="button" className="carousel-arrow" onClick={() => api?.scrollPrev()} aria-label="Show previous client story"><ArrowLeft size={17} /></button>
        <button type="button" className="carousel-arrow" onClick={() => api?.scrollNext()} aria-label="Show next client story"><ArrowRight size={17} /></button>
      </div>
    </div>
    <Carousel setApi={setApi} opts={{ align: "start", loop: true }}>
      <CarouselContent className="-ml-0">
        {stories.map((story, index) => <CarouselItem key={story.type === "quote" ? story.name : story.title} className="pl-0">
          <article className="testimonial-card">
            <span className="testimonial-card__label">{story.label}</span>
            {story.type === "quote" ? <>
              <Quote className="testimonial-card__quote-mark" size={32} aria-hidden="true" />
              <blockquote>“{story.quote}”</blockquote>
              <footer><strong>{story.name}</strong><span>{story.organization}</span></footer>
            </> : <>
              <TrendingUp className="testimonial-card__quote-mark" size={29} aria-hidden="true" />
              <strong className="testimonial-card__metric">{story.metric}</strong>
              <h3>{story.title}</h3>
              <p>{story.copy}</p>
            </>}
          </article>
        </CarouselItem>)}
      </CarouselContent>
    </Carousel>
    <div className="carousel-dots" role="tablist" aria-label="Select a client story">
      {stories.map((story, index) => <button key={story.type === "quote" ? story.name : story.title} type="button" role="tab" aria-selected={selected === index} aria-label={`Show story ${index + 1}`} className={selected === index ? "is-active" : ""} onClick={() => api?.scrollTo(index)} />)}
    </div>
  </div>;
}
